import * as THREE from "three";
import { TempVec } from "../perf/TempVec";

export interface SpringArmConfig {
  offset: THREE.Vector3;
  positionLambda?: number;
  rotationLambda?: number;
}

export class SpringArmCamera {
  readonly camera: THREE.PerspectiveCamera;
  private target: THREE.Object3D | null = null;
  private offset = new THREE.Vector3(0, 1.5, 6);
  private positionLambda = 8;
  private rotationLambda = 10;
  private worldOffset = new THREE.Vector3();
  private desiredQuat = new THREE.Quaternion();
  private lookTarget = new THREE.Vector3();

  constructor(camera: THREE.PerspectiveCamera, cfg?: Partial<SpringArmConfig>) {
    this.camera = camera;
    if (cfg?.offset) this.offset.copy(cfg.offset);
    if (typeof cfg?.positionLambda === "number") this.positionLambda = cfg.positionLambda;
    if (typeof cfg?.rotationLambda === "number") this.rotationLambda = cfg.rotationLambda;
  }

  setTarget(target: THREE.Object3D | null): void {
    this.target = target;
  }

  setOffset(offset: THREE.Vector3): void {
    this.offset.copy(offset);
  }

  setPositionLambda(value: number): void {
    this.positionLambda = value;
  }

  setRotationLambda(value: number): void {
    this.rotationLambda = value;
  }

  update(dt: number): void {
    if (!this.target) return;
    const tPos = this.target.getWorldPosition(TempVec.vec3());
    const tQuat = this.target.getWorldQuaternion(TempVec.quat());

    this.worldOffset.copy(this.offset).applyQuaternion(tQuat).add(tPos);
    const posT = 1 - Math.exp(-this.positionLambda * dt);
    this.camera.position.lerp(this.worldOffset, posT);

    this.lookTarget.copy(tPos);
    const m = TempVec.mat4().lookAt(this.camera.position, this.lookTarget, TempVec.vec3().set(0, 1, 0).applyQuaternion(tQuat));
    this.desiredQuat.setFromRotationMatrix(m);
    const rotT = 1 - Math.exp(-this.rotationLambda * dt);
    this.camera.quaternion.slerp(this.desiredQuat, rotT);
  }

  snapToTarget(): void {
    if (!this.target) return;
    const tPos = this.target.getWorldPosition(TempVec.vec3());
    const tQuat = this.target.getWorldQuaternion(TempVec.quat());
    this.worldOffset.copy(this.offset).applyQuaternion(tQuat).add(tPos);
    this.camera.position.copy(this.worldOffset);
    this.camera.lookAt(tPos);
  }
}
